import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, BarChart3, AlertCircle, Pause } from 'lucide-react';
import api from '@/lib/api';

interface ProgressData {
  dailyLimit: number;
  currentCount: number;
  remaining: number;
  delaySeconds: number;
  percentage: number;
  connectionName: string;
  connectionId: number;
  windowStart?: string | null;
  windowEnd?: string | null;
}

interface DailyProgressVisualizationProps {
  clientId: number | null;
  selectedConnectionId?: number | null;
}

const DailyProgressVisualization: React.FC<DailyProgressVisualizationProps> = ({ clientId, selectedConnectionId }) => {
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [inactiveTime, setInactiveTime] = useState(0);
  
  // Chaves para localStorage persistente
  const getStorageKey = (key: string) => `dailyProgress_${clientId}_${key}`;
  
  // Estados persistentes no localStorage
  const [lastActivityTime, setLastActivityTime] = useState<number>(() => {
    if (!clientId) return Date.now();
    const stored = localStorage.getItem(getStorageKey('lastActivity'));
    return stored ? parseInt(stored) : Date.now();
  });
  
  const [isPaused, setIsPaused] = useState<boolean>(() => {
    if (!clientId) return false;
    const stored = localStorage.getItem(getStorageKey('isPaused'));
    return stored === 'true';
  });
  
  const [lastKnownCount, setLastKnownCount] = useState<number>(() => {
    if (!clientId) return 0;
    const stored = localStorage.getItem(getStorageKey('lastCount'));
    return stored ? parseInt(stored) : 0;
  });
  
  const [isComponentMounted, setIsComponentMounted] = useState(false);

  const loadProgressData = async (isInitial = false) => {
    if (!clientId) {
      setProgressData(null);
      setIsInitialLoading(false);
      return;
    }

    try {
      if (isInitial) {
        setIsInitialLoading(true);
      } else {
        setIsUpdating(true);
      }
      
      console.log('🔍 [DailyProgress] Carregando dados para cliente:', clientId);
      
      // Buscar dados do Rate Limits (mesma fonte da API de Envio)
      const response = await api.get(`/rate-limits/redis/client/${clientId}`);
      console.log('📊 [DailyProgress] Resposta do Rate Limits:', response);
      
      if (response.success && response.limits && response.limits.length > 0) {
        // Usar a conexão selecionada ou a conexão com maior limite (fallback)
        let targetConnection;
        if (selectedConnectionId) {
          targetConnection = response.limits.find((limit: any) => 
            limit.connectionId === selectedConnectionId
          );
        }
        
        // Se não encontrou a conexão selecionada, usar a com maior limite
        if (!targetConnection) {
          targetConnection = response.limits.reduce((prev: any, current: any) => {
            return (current.dailyLimit > prev.dailyLimit) ? current : prev;
          });
        }
        
        const percentage = targetConnection.dailyLimit > 0 
          ? Math.round((targetConnection.currentCount / targetConnection.dailyLimit) * 100) 
          : 0;
        
        const progressInfo: ProgressData = {
          dailyLimit: targetConnection.dailyLimit || 0,
          currentCount: targetConnection.currentCount || 0,
          remaining: targetConnection.remaining || 0,
          delaySeconds: targetConnection.delaySeconds || 0,
          percentage,
          connectionName: targetConnection.connectionName || 'Conexão',
          connectionId: targetConnection.connectionId || 0,
          windowStart: targetConnection.windowStart || null,
          windowEnd: targetConnection.windowEnd || null
        };
        
        console.log('✅ [DailyProgress] Dados processados:', progressInfo);
        
        // Detectar se houve atividade (aumento no contador)
        if (progressInfo.currentCount > lastKnownCount) {
          const now = Date.now();
          console.log('🚀 [DailyProgress] NOVA ATIVIDADE detectada! Novo envio realizado.');
          console.log(`📊 [DailyProgress] Contador: ${lastKnownCount} → ${progressInfo.currentCount}`);
          
          setLastActivityTime(now);
          setInactiveTime(0);
          setIsPaused(false);
          setLastKnownCount(progressInfo.currentCount);
          
          // Persistir no localStorage
          localStorage.setItem(getStorageKey('lastActivity'), now.toString());
          localStorage.setItem(getStorageKey('isPaused'), 'false');
          localStorage.setItem(getStorageKey('lastCount'), progressInfo.currentCount.toString());
        } else if (isInitial) {
          // Na primeira carga, sincronizar com localStorage
          console.log(`🔍 [DailyProgress] Primeira carga - definindo contador inicial: ${progressInfo.currentCount}`);
          setLastKnownCount(progressInfo.currentCount);
          localStorage.setItem(getStorageKey('lastCount'), progressInfo.currentCount.toString());
          
          // Inicializar lastActivityTime se não existir
          const storedLastActivity = localStorage.getItem(getStorageKey('lastActivity'));
          if (!storedLastActivity) {
            const now = Date.now();
            setLastActivityTime(now);
            localStorage.setItem(getStorageKey('lastActivity'), now.toString());
            console.log(`⏰ [DailyProgress] Inicializando lastActivityTime: ${new Date(now).toLocaleTimeString()}`);
          }
        } else {
          // Sem atividade nova - apenas log para debug
          console.log(`📊 [DailyProgress] Sem nova atividade. Contador mantido: ${progressInfo.currentCount}`);
        }
        
        setProgressData(progressInfo);
      } else {
        console.log('❌ [DailyProgress] Nenhuma conexão encontrada');
        if (!progressData) {
          setProgressData(null);
        }
      }
    } catch (error) {
      console.error('❌ [DailyProgress] Erro ao carregar dados:', error);
      if (!progressData) {
        setProgressData(null);
      }
    } finally {
      setIsInitialLoading(false);
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    // Marcar componente como montado para evitar reinicializações
    setIsComponentMounted(true);
    
    loadProgressData(true);
    
    // CORREÇÃO: Verificar estado pausado ANTES de configurar interval
    const storedPaused = localStorage.getItem(getStorageKey('isPaused'));
    const storedLastActivity = localStorage.getItem(getStorageKey('lastActivity'));
    
    if (storedPaused === 'true' && storedLastActivity) {
      const timeSinceLastActivity = Date.now() - parseInt(storedLastActivity);
      if (timeSinceLastActivity > 60000) {
        console.log('⏸️ [DailyProgress] Componente montado - mantendo estado pausado');
        setIsPaused(true);
        return; // NÃO configurar interval se está pausado
      }
    }
    
    // Atualizar com base no delay configurado ou mínimo de 5 segundos
    // Se não há progressData ainda, usar intervalo padrão de 6 segundos
    const delaySeconds = progressData?.delaySeconds || 6;
    const updateInterval = Math.max(delaySeconds * 1000, 5000);
    
    console.log(`⏰ [DailyProgress] Configurando interval de ${updateInterval/1000}s (delay: ${delaySeconds}s)`);
    
    const interval = setInterval(() => {
      // CORREÇÃO: Re-verificar estado pausado do localStorage a cada execução
      const currentPausedState = localStorage.getItem(getStorageKey('isPaused'));
      if (currentPausedState === 'true') {
        console.log('⏸️ [DailyProgress] Interval executado mas estado é pausado - IGNORANDO');
        return;
      }
      
      const storedLastActivity = localStorage.getItem(getStorageKey('lastActivity'));
      const lastActivity = storedLastActivity ? parseInt(storedLastActivity) : Date.now();
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      const inactiveSeconds = Math.floor(timeSinceLastActivity / 1000);
      
      setInactiveTime(inactiveSeconds);
      
      // Log para debug
      if (inactiveSeconds > 30 && inactiveSeconds % 10 === 0) {
        console.log(`⏱️ [DailyProgress] ${inactiveSeconds}s sem atividade (limite: 60s)`);
      }
      
      // Pausar atualizações após 60 segundos sem atividade
      if (timeSinceLastActivity > 60000) { // 60 segundos
        console.log(`⏸️ [DailyProgress] Pausando atualizações após ${inactiveSeconds}s sem atividade`);
        console.log(`⏸️ [DailyProgress] Última atividade: ${new Date(lastActivity).toLocaleTimeString()}`);
        setIsPaused(true);
        localStorage.setItem(getStorageKey('isPaused'), 'true');
        return; // Não fazer requisição
      }
      
      console.log('🔄 [DailyProgress] Executando atualização normal');
      loadProgressData(false);
    }, updateInterval);
    
    return () => clearInterval(interval);
  }, [clientId, progressData?.delaySeconds, selectedConnectionId]);

  // useEffect separado para reconfigurar interval quando delay muda
  useEffect(() => {
    if (!progressData || isPaused) return;
    
    console.log(`🔄 [DailyProgress] Delay atualizado para ${progressData.delaySeconds}s - reconfigurando interval`);
  }, [progressData?.delaySeconds, isPaused]);

  // Verificar periodicamente se deve retomar atualizações (quando pausado)
  useEffect(() => {
    if (!isPaused || !clientId) return;
    
    // Verificar a cada 30 segundos se houve atividade quando pausado
    const resumeCheckInterval = setInterval(async () => {
      console.log('🔍 [DailyProgress] Verificando atividade durante pausa...');
      
      try {
        const response = await api.get(`/rate-limits/redis/client/${clientId}`);
        if (response.success && response.limits && response.limits.length > 0) {
          // Usar a mesma lógica de seleção de conexão
          let targetConnection;
          if (selectedConnectionId) {
            targetConnection = response.limits.find((limit: any) => 
              limit.connectionId === selectedConnectionId
            );
          }
          
          if (!targetConnection) {
            targetConnection = response.limits.reduce((prev: any, current: any) => {
              return (current.dailyLimit > prev.dailyLimit) ? current : prev;
            });
          }
          
          // Se contador aumentou, retomar atualizações
          if (targetConnection.currentCount > lastKnownCount) {
            const now = Date.now();
            console.log('🚀 [DailyProgress] NOVO DISPARO detectado durante pausa! RETOMANDO monitoramento...');
            console.log(`📊 [DailyProgress] Contador: ${lastKnownCount} → ${targetConnection.currentCount}`);
            console.log(`🔄 [DailyProgress] Voltando ao estado ATIVO`);
            
            setLastActivityTime(now);
            setInactiveTime(0);
            setIsPaused(false);
            setLastKnownCount(targetConnection.currentCount);
            
            // Persistir no localStorage
            localStorage.setItem(getStorageKey('lastActivity'), now.toString());
            localStorage.setItem(getStorageKey('isPaused'), 'false');
            localStorage.setItem(getStorageKey('lastCount'), targetConnection.currentCount.toString());
            
            // Atualizar dados imediatamente
            loadProgressData(false);
          }
        }
      } catch (error) {
        console.error('❌ [DailyProgress] Erro na verificação de retomada:', error);
      }
    }, 30000); // Verificar a cada 30 segundos
    
    return () => clearInterval(resumeCheckInterval);
  }, [isPaused, clientId, lastKnownCount]);

  // Verificar estado inicial APENAS uma vez ao montar componente
  useEffect(() => {
    if (!clientId) return;
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityTime;
    
    console.log('🔍 [DailyProgress] Estado inicial carregado do localStorage:', {
      lastActivityTime: new Date(lastActivityTime).toLocaleTimeString(),
      isPaused,
      lastKnownCount,
      timeSinceLastActivity: Math.floor(timeSinceLastActivity / 1000) + 's'
    });
    
    // Se passou mais de 60s desde a última atividade, manter pausado
    if (timeSinceLastActivity > 60000 && !isPaused) {
      console.log('⏸️ [DailyProgress] Mantendo estado pausado (tempo expirado)');
      setIsPaused(true);
      localStorage.setItem(getStorageKey('isPaused'), 'true');
    }
  // CORREÇÃO: Dependência apenas de clientId para evitar re-execução
  }, [clientId]);

  // Prevenir reinicialização por eventos de focus/blur/screenshot
  useEffect(() => {
    const handleVisibilityChange = () => {
      console.log('👁️ [DailyProgress] Visibility change detectado - IGNORANDO (mantendo estado)');
      // Não fazer nada - manter estado atual
    };

    const handleFocus = () => {
      console.log('🎯 [DailyProgress] Focus detectado - IGNORANDO (mantendo estado)');
      // Não fazer nada - manter estado atual
    };

    const handleBlur = () => {
      console.log('🌫️ [DailyProgress] Blur detectado - IGNORANDO (mantendo estado)');
      // Não fazer nada - manter estado atual
    };

    // Adicionar listeners que NÃO fazem nada (previnem outros listeners)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  if (!clientId) {
    return (
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="text-center text-gray-500">
            <span className="text-sm">Selecione um cliente para ver o progresso diário</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isInitialLoading || !progressData) {
    return (
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
            <span className="text-sm">Carregando progresso diário...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusIcon = () => {
    if (isPaused) {
      return <Pause className="h-4 w-4 text-gray-500" />;
    } else if (progressData.percentage >= 100) {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    } else if (progressData.percentage >= 80) {
      return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    } else {
      return <BarChart3 className="h-4 w-4 text-green-500" />;
    }
  };

  const getStatusText = () => {
    if (isPaused) {
      return 'Inativo (Sem Novos Envios)';
    } else if (progressData.percentage >= 100) {
      return 'Limite Atingido';
    } else if (progressData.percentage >= 80) {
      return 'Próximo do Limite';
    } else {
      return 'Operando';
    }
  };

  const getStatusColor = () => {
    if (isPaused) {
      return 'bg-gray-500';
    } else if (progressData.percentage >= 100) {
      return 'bg-red-500';
    } else if (progressData.percentage >= 80) {
      return 'bg-yellow-500';
    } else {
      return 'bg-green-500';
    }
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {getStatusIcon()}
              <span className="text-sm font-medium">Progresso Diário</span>
              <Badge variant="outline" className={`text-white ${getStatusColor()}`}>
                {getStatusText()}
              </Badge>
              {isUpdating && (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{progressData.currentCount}</span> / {progressData.dailyLimit} enviados
              </div>
              
              {progressData.delaySeconds > 0 && !isPaused && (
                <div className="flex items-center space-x-1 text-sm text-gray-600">
                  <Clock className="h-3 w-3" />
                  <span>Delay: {progressData.delaySeconds}s</span>
                </div>
              )}

              {progressData.windowStart && progressData.windowEnd && (
                <div className="flex items-center space-x-1 text-sm text-gray-600">
                  <Clock className="h-3 w-3" />
                  <span>
                    Janela: {progressData.windowStart} - {progressData.windowEnd}
                  </span>
                </div>
              )}

              {isPaused && inactiveTime > 0 && (
                <div className="flex items-center space-x-1 text-sm text-gray-500">
                  <Clock className="h-3 w-3" />
                  <span>Inativo há {Math.floor(inactiveTime / 60)}m {inactiveTime % 60}s</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="text-right text-sm text-gray-600">
            <div>{progressData.percentage}% do limite diário</div>
            <div className="text-xs">{progressData.connectionName}</div>
          </div>
        </div>
        
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-green-500 h-3 rounded-full transition-all duration-500 ease-in-out"
              style={{ width: `${Math.min(progressData.percentage, 100)}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyProgressVisualization;
